import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Loader2,
  Save,
  KeyRound,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const KEYS = [
  {
    id: "google_maps_api_key",
    label: "Google Maps API Key",
    hint: "Used server-side to query the Places API (New). Never exposed to mobile clients.",
    docs: "https://console.cloud.google.com/apis/credentials",
    docsLabel: "Get key from Google Cloud Console",
    required: ["Places API (New)", "Maps SDK for Android", "Maps SDK for iOS"],
  },
  {
    id: "directions_api_key",
    label: "Google Directions API Key",
    hint: "Used for turn-by-turn directions and route computation. Can be the same key as Maps if Directions API is enabled.",
    docs: "https://console.cloud.google.com/apis/library/directions-backend.googleapis.com",
    docsLabel: "Enable Directions API",
  },
  {
    id: "weather_api_key",
    label: "OpenWeatherMap API Key",
    hint: "Powers the mobile app's weather widget. Sign up for a free key (60 calls/min).",
    docs: "https://openweathermap.org/appid",
    docsLabel: "Get OpenWeatherMap key",
  },
  {
    id: "admob_android_app_id",
    label: "AdMob Android App ID",
    hint: "Ships to the mobile app for AdMob initialisation.",
    docs: "https://apps.admob.com/",
    docsLabel: "Get from AdMob console",
  },
  {
    id: "admob_ios_app_id",
    label: "AdMob iOS App ID",
    hint: "Ships to the mobile app for AdMob initialisation.",
    docs: "https://apps.admob.com/",
    docsLabel: "Get from AdMob console",
  },
  {
    id: "backend_url",
    label: "Backend URL (mobile app)",
    hint: "Public backend URL the mobile app calls. Update if you deploy the backend to a new domain — no code change required.",
  },
  {
    id: "google_oauth_client_id",
    label: "Google OAuth — Client ID",
    hint: "Enables first-party 'Sign in with Google' (removes the Emergent-branded auth page). Create an OAuth 2.0 Client of type Web application.",
    docs: "https://console.cloud.google.com/apis/credentials",
    docsLabel: "Create OAuth Client (Google Cloud Console)",
    required: ["OAuth consent screen configured", "Type: Web application"],
  },
  {
    id: "google_oauth_client_secret",
    label: "Google OAuth — Client Secret",
    hint: "The client_secret shown next to the Client ID. Both must be set together.",
    docs: "https://console.cloud.google.com/apis/credentials",
    docsLabel: "Copy from Google Cloud Console",
  },
  {
    id: "google_oauth_web_redirect_uri",
    label: "Google OAuth — Redirect URI",
    hint: "MUST match a URI registered under 'Authorized redirect URIs' in Google Cloud. Typically your backend URL + /api/auth/google/callback.",
    docs: "https://console.cloud.google.com/apis/credentials",
    docsLabel: "Add redirect URI in Google Cloud",
  },
  {
    id: "play_store_url",
    label: "Google Play — Store URL",
    hint: "Full listing URL. Activates the Play Store badge on the homepage. Leave blank to keep the badge dim.",
    docs: "https://play.google.com/console",
    docsLabel: "Google Play Console",
  },
  {
    id: "apple_store_url",
    label: "Apple App Store — URL",
    hint: "Full https://apps.apple.com/… URL. Activates the App Store badge on the homepage. Leave blank to keep the badge dim.",
    docs: "https://appstoreconnect.apple.com/",
    docsLabel: "App Store Connect",
  },
];

export default function Secrets() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inputs, setInputs] = useState({});
  const [shown, setShown] = useState({});
  // Cache of revealed plaintext values keyed by secret id. Cleared on unmount.
  const [revealed, setRevealed] = useState({});
  const [revealBusy, setRevealBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/secrets");
      setState(data);
    } catch (e) {
      toast.error("Failed to load secrets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (id) => {
    const val = inputs[id];
    if (val === undefined) return;
    setSaving(true);
    try {
      const { data } = await api.put("/secrets", { [id]: val });
      setState(data);
      setInputs((prev) => ({ ...prev, [id]: "" }));
      toast.success("Secret saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clear = async (id) => {
    if (!confirm("Clear this secret? The app will fall back to the .env value if any.")) return;
    setSaving(true);
    try {
      const { data } = await api.put("/secrets", { [id]: "" });
      setState(data);
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.success("Secret cleared");
    } catch (e) {
      toast.error("Clear failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleReveal = async (id) => {
    if (revealed[id] !== undefined) {
      // Already revealed — collapse back to masked view.
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    setRevealBusy(id);
    try {
      const { data } = await api.get(`/secrets/${id}/reveal`);
      setRevealed((prev) => ({ ...prev, [id]: data.value || "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reveal failed");
    } finally {
      setRevealBusy(null);
    }
  };

  const copyValue = async (id) => {
    const val = revealed[id];
    if (!val) return;
    try {
      await navigator.clipboard.writeText(val);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Clipboard blocked — select and copy manually");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl">
      <div className="mb-8 animate-in-up">
        <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
          Secrets vault
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
          API Keys &amp; Credentials
        </h1>
        <p className="text-sm text-muted-foreground max-w-xl">
          Manage the credentials NearMe uses to talk to Google, AdMob and other
          providers. Values entered here are stored in MongoDB and take precedence
          over <span className="mono">.env</span> values.
        </p>
      </div>

      {loading || !state ? (
        <div className="text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-5 stagger">
          {KEYS.map((k) => {
            const s = state[k.id] || {};
            const isSet = s.set;
            const inputVal = inputs[k.id] ?? "";
            const showRaw = !!shown[k.id];
            return (
              <div
                key={k.id}
                data-testid={`secret-card-${k.id}`}
                className="bg-card border border-border rounded-2xl p-6"
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                        isSet
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      <KeyRound className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-base font-bold">{k.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {k.hint}
                      </div>
                    </div>
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md ${
                      isSet
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {isSet ? (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    ) : (
                      <AlertTriangle className="w-3.5 h-3.5" />
                    )}
                    {isSet ? `Set · ${s.source}` : "Not set"}
                  </span>
                </div>

                {isSet && (
                  <div className="mb-4 flex items-center gap-2 text-sm">
                    <span className="text-xs uppercase tracking-widest text-muted-foreground">
                      Current
                    </span>
                    <span
                      className="mono px-2 py-1 rounded-md bg-secondary text-secondary-foreground break-all select-all"
                      data-testid={`secret-current-${k.id}`}
                    >
                      {revealed[k.id] !== undefined ? revealed[k.id] : s.masked}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleReveal(k.id)}
                      disabled={revealBusy === k.id}
                      data-testid={`secret-reveal-${k.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                      title={revealed[k.id] !== undefined ? "Hide" : "Reveal plaintext"}
                    >
                      {revealBusy === k.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : revealed[k.id] !== undefined ? (
                        <EyeOff className="w-3.5 h-3.5" />
                      ) : (
                        <Eye className="w-3.5 h-3.5" />
                      )}
                      {revealed[k.id] !== undefined ? "Hide" : "Reveal"}
                    </button>
                    {revealed[k.id] !== undefined && revealed[k.id] && (
                      <button
                        type="button"
                        onClick={() => copyValue(k.id)}
                        data-testid={`secret-copy-${k.id}`}
                        className="inline-flex items-center text-xs font-semibold text-primary hover:underline"
                      >
                        Copy
                      </button>
                    )}
                  </div>
                )}

                {k.required && (
                  <div className="mb-4 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">
                      Enable in Google Cloud:
                    </span>{" "}
                    {k.required.join(" · ")}
                  </div>
                )}

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {isSet ? "Replace value" : "Enter value"}
                  </Label>
                  <div className="mt-1.5 flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        type={showRaw ? "text" : "password"}
                        value={inputVal}
                        onChange={(e) =>
                          setInputs((p) => ({ ...p, [k.id]: e.target.value }))
                        }
                        placeholder="Paste key value…"
                        data-testid={`secret-input-${k.id}`}
                        className="h-11 pr-11 mono text-sm"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShown((p) => ({ ...p, [k.id]: !p[k.id] }))
                        }
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
                        aria-label="Toggle visibility"
                      >
                        {showRaw ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <Button
                      onClick={() => save(k.id)}
                      disabled={saving || !inputVal}
                      data-testid={`secret-save-${k.id}`}
                      className="h-11"
                    >
                      <Save className="w-4 h-4 mr-2" /> Save
                    </Button>
                    {isSet && (
                      <Button
                        variant="outline"
                        onClick={() => clear(k.id)}
                        disabled={saving}
                        data-testid={`secret-clear-${k.id}`}
                        className="h-11"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {k.docs && (
                  <a
                    href={k.docs}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    {k.docsLabel} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            );
          })}

          {state.updated_at && (
            <div className="text-xs text-muted-foreground">
              Last updated: {new Date(state.updated_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
